
import { Component, Mixins, Prop, Provide } from "vue-property-decorator";
import { mapState } from "vuex";
import { DataModules } from "@/store/modules/data";
import { DelegateObjectTypes } from "@/data/enums/delegates";
import { IDelegate, IDelegateMapped } from "@/models/delegates";
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";
import _ from "@/boot/lodash";

export interface IDelegatesProvider {
  isLoading: boolean;
  isReloading: boolean;
  mappedDelegates: IDelegateMapped[];
}

export interface IDelegatesMethods {
  openInviteModal: () => void;
  sendInvite: (email: string) => Promise<any>;
  revokeAccess: (
    accessId: IDelegate["id"],
    email: IDelegateMapped["email"]
  ) => Promise<any>;
}

@Component({
  computed: {
    ...mapState({
      delegates(state, getters) {
        return getters[`data/listArray`]({
          scope: this.dataScope,
          storeModule: DataModules.DELEGATES
        });
      }
    })
  }
})
export default class DelegatesProvider extends Mixins(
  RequestPromises,
  Context
) {
  // Props
  @Prop({ required: true })
  public readonly objectId!: string;

  @Prop({
    required: true,
    validator: value => {
      return _.values(DelegateObjectTypes).includes(value) as boolean;
    }
  })
  public readonly objectType!: DelegateObjectTypes;

  // Data

  public readonly dataScope = `$${this.objectType}_${this.objectId}`;
  public readonly delegates!: IDelegate[];
  public isLoading = true;
  public isReloading = false;

  // Life-cycle Methods

  public render() {
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $delegatesData: this.delegatesProvider(),
        $delegatesMethods: this.delegatesMethods
      })
    );
  }

  public async created() {
    this.isLoading = true;
    this.getDelegates();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      scope: this.dataScope,
      storeModule: DataModules.DELEGATES,
      vm: this
    });
  }

  // Provide

  @Provide()
  public delegatesProvider(): IDelegatesProvider {
    return {
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      mappedDelegates: this.mappedDelegates
    };
  }

  @Provide()
  public get delegatesMethods(): IDelegatesMethods {
    return {
      openInviteModal: this.openInviteModal,
      sendInvite: this.sendInvite,
      revokeAccess: this.revokeAccess
    };
  }

  // Computed

  public get mappedDelegates() {
    return _.map(this.delegates, delegate => ({
      ..._.pick(delegate, ["active", "created_at", "client_id", "id"]),
      email: _.get(
        delegate,
        "client.default_email.email",
        delegate.invite_email
      ),
      image_url: _.get(delegate, "client.image_url", null),
      public_name: _.get(delegate, "client.public_name", null)
    }));
  }

  // Methods

  public async getDelegates(ignoreStored = false) {
    try {
      await this.$store.dispatch(`data/${DataModules.DELEGATES}/list`, {
        vm: this,
        scope: this.dataScope,
        ignoreStored,
        params: {
          ["filter[object_id]"]: this.objectId,
          ["filter[object_type]"]: this.objectType,
          order: "-active",
          with: "client,client.image",
          with_staged_imports: 1
        },
        returnData: false
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public refreshDelegates() {
    this.isReloading = true;
    return this.getDelegates(true);
  }

  public openInviteModal() {
    this.$store.dispatch("ui/open/windowModal", {
      config: {
        component: () =>
          import("@/components/app/global/delegates/delegateInviteModal.vue"),
        width: 420,
        canCancel: ["button"],
        parent: this
      }
    });
  }

  public async sendInvite(email) {
    await this.$store.dispatch(`data/${DataModules.DELEGATES}/sendInvite`, {
      data: {
        object_id: this.objectId,
        object_type: this.objectType,
        email
      }
    });
    return this.refreshDelegates();
  }

  public async revokeAccess(accessId, email) {
    try {
      await this.$store.dispatch(`data/${DataModules.DELEGATES}/revokeAccess`, {
        accessId
      });
      this.$buefy.toast.open({
        message: this.$t("_sentence.delegates.access_revoked_for", {
          delegate: email
        }) as string
      });
      return this.refreshDelegates();
    } catch (error) {
      return this.$store.dispatch("api/handleError", error);
    }
  }
}
